import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useFocusTrap } from '@prophecy/utils/react/focus';
import { useControlledState } from '@prophecy/utils/react/hooks';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { Provider, Root, Portal, Trigger } from '@radix-ui/react-tooltip';
import React, { isValidElement } from 'react';
import styled from 'styled-components';
import { CopyAIcon } from '../Icons';
import { Stack } from '../Layout';
import { Markdown } from '../Markdown';
import { theme } from '../theme';
import { StyledContent, StyledArrow, tokens } from './styled';
import { TooltipVariant } from './types';
const StyledMarkDown = styled(Markdown)`
  color: inherit;
`;
const StyledCopyIcon = styled(CopyAIcon)`
  cursor: pointer;
  z-index: ${theme.zLayer.l};
  background: inherit;
`;
export function Tooltip({ title, visible, onVisibleChange, children, overlayClassName, overlayStyle, arrow = true, placement: side = 'top', placementOffset: sideOffset = 8, align, hasCopy = false, alignOffset, delay = 100, variant = TooltipVariant.default, disabled, isMarkdown = false, forceTooltipContentColor = variant === TooltipVariant.default, ...contentProps }) {
    const [_visible, toggleVisible] = useControlledState({
        value: !title || disabled ? false : visible,
        onChange: onVisibleChange
    });
    const titleRef = /*#__PURE__*/ React.createRef();
    const container = useFocusTrap(Boolean(_visible));
    const onCopy = async (e)=>{
        e.stopPropagation();
        const text = titleRef?.current?.innerText || '';
        await navigator.clipboard.writeText(text);
    };
    const content = /*#__PURE__*/ _jsxs(StyledContent, {
        variant: variant,
        className: overlayClassName,
        $forceTooltipContentColor: forceTooltipContentColor,
        style: overlayStyle,
        side: side,
        align: align,
        sideOffset: sideOffset,
        alignOffset: alignOffset,
        ref: container,
        tabIndex: -1,
        ...contentProps,
        children: [
            arrow && /*#__PURE__*/ _jsx(StyledArrow, {
                variant: variant,
                offset: parseInt(tokens.Arrow.offset, 10)
            }),
            /*#__PURE__*/ _jsxs(Stack, {
                direction: "horizontal",
                width: "100%",
                align: "space-between",
                gap: theme.spaces.x4,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        ref: titleRef,
                        children: isMarkdown ? /*#__PURE__*/ _jsx(StyledMarkDown, {
                            children: title
                        }) : title
                    }),
                    hasCopy && title && /*#__PURE__*/ _jsx(StyledCopyIcon, {
                        size: "xs",
                        type: "default",
                        onClick: onCopy
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ _jsx(Provider, {
        children: /*#__PURE__*/ _jsxs(Root, {
            open: _visible,
            delayDuration: delay,
            onOpenChange: toggleVisible,
            children: [
                /*#__PURE__*/ _jsx(Trigger, {
                    asChild: true,
                    children: children
                }),
                /*#__PURE__*/ _jsx(Portal, {
                    children: /*#__PURE__*/ _jsx(FocusScope, {
                        asChild: true,
                        trapped: false,
                        onUnmountAutoFocus: (e)=>e.preventDefault(),
                        onMountAutoFocus: (e)=>e.preventDefault(),
                        children: content
                    })
                })
            ]
        })
    });
}
export const renderWithTooltip = (content, tooltip)=>{
    if (!tooltip) return content;
    const tooltipProps = /*#__PURE__*/ isValidElement(tooltip) || typeof tooltip === 'string' ? {
        title: tooltip,
        placement: 'top',
        isMarkdown: typeof tooltip === 'string',
        overlayStyle: {
            maxWidth: '500px',
            maxHeight: '300px',
            overflow: 'auto',
            padding: theme.spaces.x12
        }
    } : tooltip;
    return /*#__PURE__*/ _jsx(Tooltip, {
        ...tooltipProps,
        children: content
    });
};
